/**
 * analytics.ts — EV-54: first-party fire-and-forget analytics tracker.
 *
 * Design rules (PERFORMANCE LAW):
 *   • navigator.sendBeacon (fallback: fetch keepalive:true)
 *   • NEVER awaited, NEVER blocks render
 *   • No-op when analytics consent not granted
 *   • All errors swallowed — analytics MUST NOT break the app
 *
 * Usage:
 *   trackPageview(path?)          — call on every route change
 *   trackEvent(type, extra?)      — call for any named event
 *   track(type, extra?)           — low-level, used by the above two
 *
 * Backend endpoint: POST /api/collect  (existing, kept for compat)
 *                   POST /api/track    (new EV-54 enriched endpoint)
 *   We send to /api/collect for now (the existing backend handler) so this is
 *   a drop-in replacement for the old analytics.js. The backend team can add
 *   /api/track later; switch ENDPOINT below.
 */

import { getDeviceId } from './deviceId';
import { hasAnalyticsConsent } from '../components/Common/CookieConsent';

// ─── Configuration ────────────────────────────────────────────────────────────

// Resolve backend base URL using the same pattern as PaymentPage/WifiPage/etc.
function getBackendBase(): string {
  try {
    const envUrl = (import.meta as unknown as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL;
    if (envUrl) return String(envUrl).replace(/\/$/, '');
    if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host === 'localhost' || host.includes('tail372ac1.ts.net')) return '';
      return 'https://core.honestlee.app';
    }
  } catch { /* ignore */ }
  return 'https://core.honestlee.app';
}

// EV-54: send to the enriched /api/track handler (sessions + UA + IP-geo +
// ISP/asn enrichment). The legacy /api/collect handler stays mounted for any
// cached old clients; both write to the unified analytics.events table.
const ENDPOINT = `${getBackendBase()}/api/track`;

// ─── AN-1498: which HonestLee property is this? ───────────────────────────────
// Mirrors deriveSite() in apps/website-shared/hl-track.js — keep the two in sync
// so the `site` dimension in analytics.sessions/events has one vocabulary.
function getSite(): string {
  try {
    const h = (window.location.hostname || '').toLowerCase().replace(/^www\./, '');
    if (h === 'vn.honestlee.app' || h === 'thai.honestlee.app') return 'pwa';
    if (h === 'stg.honestlee.app' || h.indexOf('staging.') === 0) return 'pwa-staging';
    if (h.indexOf('hl-consumer') === 0) return 'pwa-preview';
    if (h === 'localhost' || h === '127.0.0.1') return 'local';
    return h || 'pwa';
  } catch { return 'pwa'; }
}

function getHost(): string {
  try { return (window.location.hostname || '').toLowerCase().replace(/^www\./, ''); } catch { return ''; }
}

// ─── Session & UTM capture ────────────────────────────────────────────────────

function ensureSessionId(): string {
  try {
    let sid = sessionStorage.getItem('hl_sid');
    if (!sid) {
      sid = typeof crypto !== 'undefined' && (crypto as unknown as { randomUUID?: () => string }).randomUUID
        ? (crypto as unknown as { randomUUID: () => string }).randomUUID()
        : 'sid-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem('hl_sid', sid);
    }
    return sid;
  } catch {
    return 'err-' + Math.random().toString(36).slice(2);
  }
}

interface SessionMeta {
  referrer: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  qr_code: string;
  ref_token: string;
  captured: boolean;
}

// Captured once per session on first trackPageview call.
let _sessionMeta: SessionMeta | null = null;

function captureSessionMeta(): SessionMeta {
  if (_sessionMeta) return _sessionMeta;
  try {
    const u = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    _sessionMeta = {
      referrer: (typeof document !== 'undefined' ? document.referrer : '') || '',
      utm_source: u.get('utm_source') || '',
      utm_medium: u.get('utm_medium') || '',
      utm_campaign: u.get('utm_campaign') || '',
      // `h` is what hlee.app actually sends. Every printed QR redirects to
      // /at/<slug>?h=<CODE> (hleeRoutes /r/:code), so without `h` here the
      // whole QR attribution chain silently produced NULL — analytics.sessions
      // .qr_code was 0 of 1675 rows despite the backend supporting it.
      qr_code: u.get('qr') || u.get('qr_code') || u.get('h') || '',
      ref_token: u.get('ref') || '',
      captured: true,
    };
  } catch {
    _sessionMeta = {
      referrer: '', utm_source: '', utm_medium: '', utm_campaign: '',
      qr_code: '', ref_token: '', captured: false,
    };
  }
  return _sessionMeta;
}

// ─── PWA launch surface (EV-820) ──────────────────────────────────────────────
// We do NOT rely on catching the install moment (iOS fires no install event at
// all). Instead every beacon reports HOW the app is currently running: a tab
// ('browser') vs an installed home-screen launch ('standalone'/'minimal-ui'/
// 'fullscreen', or iOS navigator.standalone, or an Android TWA). The backend
// flags a visitor "installed" if ANY of their sessions was standalone, so an
// already-installed user is discovered the next time they open from the icon.
function getDisplayMode(): { display_mode: string; is_standalone: boolean } {
  try {
    if (typeof window === 'undefined') return { display_mode: 'browser', is_standalone: false };
    // iOS Safari: the only standalone tell is the legacy navigator.standalone.
    const iosStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    // Android TWA wrapper launches arrive via the android-app referrer.
    const isTwa = typeof document !== 'undefined' && document.referrer.startsWith('android-app://');
    const mm = (q: string) => { try { return window.matchMedia(q).matches; } catch { return false; } };
    let mode = 'browser';
    if (isTwa) mode = 'twa';
    else if (mm('(display-mode: fullscreen)')) mode = 'fullscreen';
    else if (mm('(display-mode: standalone)') || iosStandalone) mode = 'standalone';
    else if (mm('(display-mode: minimal-ui)')) mode = 'minimal-ui';
    const is_standalone = mode !== 'browser';
    return { display_mode: mode, is_standalone };
  } catch {
    return { display_mode: 'browser', is_standalone: false };
  }
}

// ─── GPS piggyback (EV-1248) ──────────────────────────────────────────────────
// analytics.sessions has gps_lat/lng/accuracy_m columns but no beacon ever sent
// gps (0/1323 sessions filled). Piggyback ONLY: if geolocation permission is
// ALREADY granted (the map asks for it), grab one cached fix and attach it to
// every subsequent beacon. NEVER prompts — a tracker must not trigger the
// permission dialog.
let _gps: { lat: number; lng: number; acc?: number } | null = null;
let _gpsAttempted = false;

function maybeCaptureGps(): void {
  if (_gpsAttempted) return;
  _gpsAttempted = true;
  try {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    const perms = (navigator as unknown as { permissions?: { query: (d: { name: string }) => Promise<{ state: string }> } }).permissions;
    if (!perms?.query) return;
    perms.query({ name: 'geolocation' }).then((st) => {
      if (st.state !== 'granted') return; // never prompt from the tracker
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          _gps = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            acc: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy) : undefined,
          };
          // One dedicated beacon so the session row gets the fix even if the
          // user never navigates again (the session UPSERT keeps first non-null).
          track('gps');
        },
        () => { /* swallow — analytics must never break the app */ },
        { maximumAge: 5 * 60 * 1000, timeout: 8000, enableHighAccuracy: false }
      );
    }).catch(() => { /* ignore */ });
  } catch { /* ignore */ }
}

// ─── User context (read from the Zustand store without importing it) ──────────

function getUserId(): string | undefined {
  try {
    const raw = localStorage.getItem('hl_user');
    if (!raw) return undefined;
    const u = JSON.parse(raw);
    if (u && (u.id || u.userId)) return String(u.id ?? u.userId);
  } catch { /* ignore */ }
  return undefined;
}

// ─── Per-page timing ──────────────────────────────────────────────────────────

interface PageState {
  path: string;
  enteredAt: number;
}
let _page: PageState | null = null;
let _prevPath: string | undefined;

function flushTiming(): void {
  if (!_page) return;
  const dur = Date.now() - _page.enteredAt;
  if (dur >= 250) {
    // Fire a timing beacon — no consent re-check here; if they consented at
    // page entry we honour that even if they revoke mid-session (unlikely
    // and overly complex to handle).
    sendBeacon({
      type: 'timing',
      path: _page.path,
      duration_ms: dur,
    });
  }
  _prevPath = _page.path;
  _page = null;
}

// ─── Beacon sender ────────────────────────────────────────────────────────────

interface BeaconPayload {
  type: string;
  path?: string;
  prev_path?: string;
  duration_ms?: number;
  title?: string;
  dwell_ms?: number;
  screen?: { w: number; h: number };
  viewport?: { w: number; h: number };
  lang?: string;
  tz?: string;
  [key: string]: unknown;
}

function sendBeacon(payload: BeaconPayload): void {
  try {
    maybeCaptureGps(); // one-shot; only fires if permission is already granted
    const meta = captureSessionMeta();
    const uid = getUserId();
    const body = JSON.stringify({
      // identifiers
      session_id: ensureSessionId(),
      device_id: getDeviceId(),
      ...(uid !== undefined ? { user_id: uid } : {}),
      // legacy fields kept for the existing /api/collect handler
      sid: ensureSessionId(),
      vid: getDeviceId(),
      uid: uid || '',
      // consent flag (always true when we reach here)
      consent: true,
      // AN-1498 — WHICH PROPERTY. Lets the funnel separate marketing-site
      // traffic from PWA traffic, and see the hops between them.
      site: getSite(),
      host: getHost(),
      // screen & viewport
      screen: { w: typeof screen !== 'undefined' ? screen.width : 0, h: typeof screen !== 'undefined' ? screen.height : 0 },
      viewport: { w: typeof window !== 'undefined' ? window.innerWidth : 0, h: typeof window !== 'undefined' ? window.innerHeight : 0 },
      // locale
      lang: typeof navigator !== 'undefined' ? navigator.language : '',
      tz: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : '',
      // PWA launch surface (EV-820) — installed vs browser tab
      ...getDisplayMode(),
      // EV-1248: GPS fix, only when geolocation permission was already granted
      ...(_gps ? { gps: _gps } : {}),
      // EV-825: in-app i18n language the user has selected (react-i18next stores
      // it in localStorage as i18nextLng). Distinct from `lang` (device locale)
      // so we can see "device ru-RU → reading the app in English".
      app_lang: (() => { try { return (localStorage.getItem('i18nextLng') || '').slice(0, 12) || undefined; } catch { return undefined; } })(),
      // UTM / referral (included on every beacon for join-simplicity on backend)
      referrer: meta.referrer,
      utm_source: meta.utm_source,
      utm_medium: meta.utm_medium,
      utm_campaign: meta.utm_campaign,
      qr_code: meta.qr_code,
      ref_token: meta.ref_token,
      // the actual payload
      ...payload,
    });
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
    } else if (typeof fetch !== 'undefined') {
      fetch(ENDPOINT, {
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      }).catch(() => { /* fire and forget */ });
    }
  } catch { /* analytics must never break the app */ }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Low-level send — no-op without analytics consent. */
export function track(type: string, extra?: Record<string, unknown>): void {
  try {
    if (!hasAnalyticsConsent()) return;
    sendBeacon({ type, ...extra });
  } catch { /* never throw */ }
}

/** Fire a pageview beacon. Call on every route change. */
export function trackPageview(path?: string): void {
  try {
    flushTiming();                           // record time on the page we're leaving
    const p = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
    const title = typeof document !== 'undefined' ? (document.title || '').slice(0, 200) : '';

    if (!hasAnalyticsConsent()) {
      // Still track page state for timing even without consent;
      // flushTiming above won't send because the consent check is in sendBeacon.
      // We just update internal state so dwell_ms works if user accepts later.
      _page = { path: p, enteredAt: Date.now() };
      return;
    }

    sendBeacon({
      type: 'pageview',
      path: p,
      prev_path: _prevPath,
      title,
    });
    _page = { path: p, enteredAt: Date.now() };
  } catch { /* never throw */ }
}

/** Named event helper (click, submit, etc.). */
export function trackEvent(type: string, extra?: Record<string, unknown>): void {
  track(type, extra);
}

// ─── Tab close / background flush ────────────────────────────────────────────

if (typeof window !== 'undefined' && !(window as unknown as { __hlAnalyticsBound?: boolean }).__hlAnalyticsBound) {
  (window as unknown as { __hlAnalyticsBound: boolean }).__hlAnalyticsBound = true;
  window.addEventListener('pagehide', flushTiming);
  window.addEventListener('beforeunload', flushTiming);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushTiming();
  });

  // EV-820: Android/Chrome fires `appinstalled` at the exact install moment.
  // Capture it as a precise one-off event (iOS gives us nothing here — there
  // the standalone display-mode on the next launch is the only signal).
  window.addEventListener('appinstalled', () => {
    try { track('app_installed', { display_mode: 'standalone', is_standalone: true }); } catch { /* ignore */ }
  });

  // AN-1498 — outbound handoff to another HonestLee property.
  // Patrick 2026-08-03: "later we will have a link from our PWA to the website
  // as well" — so the PWA must decorate its outbound links exactly like the
  // marketing sites decorate theirs, or the return trip is invisible.
  //
  // For *.honestlee.app the shared hl_vid cookie already stitches the visitor,
  // so the param is belt-and-braces; for hl-website-v2.tech / nexus22.tech
  // (different registrable domain, no shared cookie possible) it is the ONLY
  // mechanism. Runs in the capture phase so it beats any app-level handler.
  const HL_FAMILY = new Set([
    'honestlee.app', 'web.honestlee.app', 'vn.honestlee.app', 'thai.honestlee.app',
    'stg.honestlee.app', 'share.honestlee.app', 'hlee.app',
    'hl-website-v2.tech', 'nexus22.tech',
  ]);

  document.addEventListener('click', (e) => {
    try {
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const raw = a.getAttribute('href') || '';
      if (!raw || raw.startsWith('#') || /^(mailto:|tel:|javascript:)/i.test(raw)) return;
      const url = new URL(raw, window.location.href);
      const target = url.hostname.toLowerCase().replace(/^www\./, '');
      if (!HL_FAMILY.has(target) || target === getHost()) return;

      url.searchParams.set('hl_vid', getDeviceId());
      if (!url.searchParams.get('utm_source')) {
        url.searchParams.set('utm_source', getSite());
        url.searchParams.set('utm_medium', 'internal');
        url.searchParams.set('utm_campaign', (a.dataset?.hlPlacement || 'link').slice(0, 40));
      }
      a.setAttribute('href', url.toString());

      track('outbound', { target_type: 'property', target_id: target, meta: { to_path: url.pathname } });
    } catch { /* never break a click */ }
  }, true);

  // When the user accepts/rejects AFTER the first pageview, re-send the current
  // pageview with consent=true so it's captured from that point forward.
  window.addEventListener('hl-consent-changed', () => {
    try {
      if (hasAnalyticsConsent() && _page) {
        // Re-send the current page's view now that consent is granted.
        sendBeacon({ type: 'pageview', path: _page.path, title: document.title?.slice(0, 200) || '' });
      }
    } catch { /* ignore */ }
  });
}
